/**
 * Pricebook Queries
 *
 * React Query hooks for pricebook management.
 * When shouldUseGobodo() is true, uses stripePricebookService and stripePriceService;
 * otherwise uses centralized mock data with localStorage persistence.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { persistedPricebookRecords, persistedPricebookEntries } from '../mockData/pricebooks';
import * as stripePricebookService from '../stripePricebookService';
import * as stripePriceService from '../stripePriceService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import type { Pricebook, PricebookEntry } from '../../types/index';

/**
 * Get all pricebooks
 */
export function usePricebooks() {
  return useQuery({
    queryKey: ['pricebooks'],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        return stripePricebookService.listPricebooks();
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      return persistedPricebookRecords.get();
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get single pricebook by ID
 */
export function usePricebook(id: string) {
  return useQuery({
    queryKey: ['pricebook', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        return stripePricebookService.getPricebook(id);
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const pricebooks = persistedPricebookRecords.get();
      const pricebook = pricebooks.find(pb => pb.id === id);
      if (!pricebook) throw new Error('Pricebook not found');
      return pricebook;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get pricebook entries for a pricebook
 */
export function usePricebookEntries(pricebookId: string) {
  return useQuery({
    queryKey: ['pricebookEntries', pricebookId],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        return stripePricebookService.listPricebookEntries(pricebookId);
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allEntries = persistedPricebookEntries.get();
      return allEntries.filter(entry => entry.pricebookId === pricebookId);
    },
    enabled: !!pricebookId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create pricebook mutation
 */
export function useCreatePricebook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pricebook: Omit<Pricebook, 'id' | 'createdDate' | 'lastModifiedDate' | 'createdBy'>) => {
      if (shouldUseGobodo()) {
        const payload = {
          name: pricebook.name,
          description: pricebook.description,
          isStandard: pricebook.isStandard,
          isActive: pricebook.isActive,
          accountId: pricebook.accountId,
          useStandardPricebook: pricebook.useStandardPricebook,
          partnerId: pricebook.partnerId,
        };
        return stripePricebookService.createPricebook(payload);
      }
      await new Promise(resolve => setTimeout(resolve, 500));

      const newPricebook: Pricebook = {
        ...pricebook,
        id: `pb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdDate: new Date().toISOString(),
        lastModifiedDate: new Date().toISOString(),
        createdBy: 'current_user'
      };

      persistedPricebookRecords.add(newPricebook);
      return newPricebook;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pricebooks'] });
      queryClient.invalidateQueries({ queryKey: ['pricebook', data.id] });
    },
  });
}

/**
 * Update pricebook mutation
 */
export function useUpdatePricebook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Pricebook> }) => {
      if (shouldUseGobodo()) {
        return stripePricebookService.updatePricebook(id, updates);
      }
      await new Promise(resolve => setTimeout(resolve, 500));

      const updated = persistedPricebookRecords.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) throw new Error('Pricebook not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pricebooks'] });
      queryClient.invalidateQueries({ queryKey: ['pricebook', data.id] });
    },
  });
}

/**
 * Delete pricebook mutation
 */
export function useDeletePricebook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (shouldUseGobodo()) {
        await stripePricebookService.deletePricebook(id);
        return { id };
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const success = persistedPricebookRecords.remove(id);
      if (!success) throw new Error('Pricebook not found');
      return { id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pricebooks'] });
      queryClient.invalidateQueries({ queryKey: ['pricebook', data.id] });
    },
  });
}

/**
 * Add pricebook entry mutation (creates a Stripe Price linked to the pricebook when Gobodo)
 */
export function useAddPricebookEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entry: Omit<PricebookEntry, 'id'>) => {
      if (shouldUseGobodo()) {
        const pricebookIdNum = parseInt(entry.pricebookId, 10);
        const isTiered = entry.billingScheme === 'tiered' && entry.tiers && entry.tiers.length > 0;
        const payload = {
          productId: entry.productId,
          currency: entry.currency,
          unitAmount: isTiered ? 0 : Math.round(entry.unitPrice * 100),
          active: entry.isActive,
          pricebookId: Number.isNaN(pricebookIdNum) ? undefined : pricebookIdNum,
          pricebookKey: Number.isNaN(pricebookIdNum) ? entry.pricebookId : undefined,
          ...(entry.recurring != null && { recurring: entry.recurring }),
          ...(isTiered && {
            billingScheme: 'tiered' as const,
            tiersMode: entry.tiersMode ?? 'graduated',
            tiers: entry.tiers,
          }),
        };
        const created = await stripePriceService.createPrice(payload);
        return {
          id: created.id,
          pricebookId: entry.pricebookId,
          productId: created.productId,
          unitPrice: created.unitAmount / 100,
          currency: created.currency,
          isActive: created.active,
          ...(created.recurring != null && { recurring: created.recurring }),
          ...(created.billingScheme != null && { billingScheme: created.billingScheme }),
          ...(created.tiersMode != null && { tiersMode: created.tiersMode }),
          ...(created.tiers != null && { tiers: created.tiers }),
        } as PricebookEntry;
      }
      await new Promise(resolve => setTimeout(resolve, 500));

      const newEntry: PricebookEntry = {
        ...entry,
        id: `pbe_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      persistedPricebookEntries.add(newEntry);
      return newEntry;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pricebookEntries', data.pricebookId] });
      queryClient.invalidateQueries({ queryKey: ['pricebook', data.pricebookId] });
    },
  });
}

/**
 * Update pricebook entry mutation
 */
export function useUpdatePricebookEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<PricebookEntry> }) => {
      if (shouldUseGobodo()) {
        const metadata: Record<string, string> = {};
        if (updates.effectiveDate) metadata.effectiveDate = updates.effectiveDate;
        if (updates.expirationDate) metadata.expirationDate = updates.expirationDate;
        await stripePriceService.updatePrice(id, {
          active: updates.isActive,
          metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        });
        const updated = await stripePriceService.getPrice(id);
        return {
          id: updated.id,
          pricebookId: updates.pricebookId ?? '',
          productId: updated.productId,
          unitPrice: updated.unitAmount / 100,
          currency: updated.currency,
          isActive: updated.active,
          effectiveDate: updates.effectiveDate,
          expirationDate: updates.expirationDate,
        } as PricebookEntry;
      }
      await new Promise(resolve => setTimeout(resolve, 500));

      const updated = persistedPricebookEntries.update(id, updates);
      if (!updated) throw new Error('Pricebook entry not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pricebookEntries', data.pricebookId] });
      queryClient.invalidateQueries({ queryKey: ['pricebook', data.pricebookId] });
    },
  });
}

/**
 * Delete pricebook entry mutation
 */
export function useDeletePricebookEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, pricebookId }: { id: string; pricebookId: string }) => {
      if (shouldUseGobodo()) {
        await stripePriceService.deletePrice(id);
        return { id, pricebookId };
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const success = persistedPricebookEntries.remove(id);
      if (!success) throw new Error('Pricebook entry not found');
      return { id, pricebookId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pricebookEntries', data.pricebookId] });
      queryClient.invalidateQueries({ queryKey: ['pricebook', data.pricebookId] });
    },
  });
}
